import { useEffect, useState } from "react";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { useAppStore } from "@/store";
import {
  MDSSidebarSection,
  MDSSidebarSectionHeader,
  MDSSidebarSectionItem,
  MDSSidebarSectionItemSlotName,
  MDSSidebarSectionSlotName,
} from "@/mds/components/sidebar";
import { observer } from "mobx-react-lite";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { FavoriteItem } from "@/store/favorite-items/types";
import { toJS } from "mobx";
import {
  MDSButtonGroup,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
} from "@/mds/components/buttons";
import {
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  MDSTooltip,
  MDSTooltipPlacement,
} from "@/mds";
import { SectionListTitleWrapper } from "@/components/layout/components/sidebar/sidebar-contents/sidebar-contents.style";
import { MDSSidebarSectionEmpty } from "@/mds/components/sidebar/sidebar-section-empty";
import { CollectionIcon } from "@/components/collection/CollectionIcon";
import styled from "@emotion/styled";

interface SidebarContentsPinnedItemsListProps {
  onClick?: () => void;
  sortedFavoriteItems?: FavoriteItem[];
  includeTitle?: boolean;
  indent?: boolean;
}

interface SidebarContentsPinnedItemsProps {
  onClick?: () => void;
}

export const SidebarContentsPinnedItems = observer(function SidebarContentsPinnedItems({
  onClick,
}: SidebarContentsPinnedItemsProps) {
  const { store } = useAppStore();
  const [isExpanded, setIsExpanded] = useState(true);
  const hasFavoriteItems = store.favoriteItems.sortedFavoriteItems.length > 0;

  return (
    <MDSSidebarSection isExpanded={isExpanded}>
      <MDSSidebarSectionHeader
        slot={MDSSidebarSectionSlotName.Header}
        label="Pinned"
        defaultIconKind={MdsIconKind.Thumbtack}
        isExpanded={isExpanded}
        onPress={() => setIsExpanded(!isExpanded)}
      />
      {hasFavoriteItems && <SidebarContentsPinnedItemsList onClick={onClick} indent />}
      {!hasFavoriteItems && (
        <MDSSidebarSectionEmpty>
          <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Tertiary}>
            Your pinned notes and collections appear here
          </MDSText>
        </MDSSidebarSectionEmpty>
      )}
    </MDSSidebarSection>
  );
});

export const SidebarContentsPinnedItemsList = observer(function SidebarContentsPinnedItemsList({
  onClick,
  includeTitle,

  indent,
}: SidebarContentsPinnedItemsListProps) {
  const { store } = useAppStore();
  const [pinnedItems, setPinnedItems] = useState<FavoriteItem[]>([]);

  useEffect(() => {
    const favoriteItems = store.favoriteItems.sortedFavoriteItems;
    setPinnedItems(toJS(favoriteItems));
  }, [store.favoriteItems.sortedFavoriteItems]);

  const handleDragEnd = (result: DropResult) => {
    const { destination, source } = result;
    if (!destination || source.index === destination.index) return;

    // Manually reorder the pinned items in local state to avoid flashing
    const newSortKey = store.favoriteItems.reorderFavoriteItems(destination, source);
    if (newSortKey) {
      const newSortedItems = [...pinnedItems];
      newSortedItems[source.index].sortKey = newSortKey;
      const sortedItems = newSortedItems.sort((a, b) => (a.sortKey < b.sortKey ? -1 : 1));
      setPinnedItems(sortedItems);
    }
  };

  return (
    <>
      {includeTitle && (
        <SectionListTitleWrapper>
          <MDSText
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Pinned Items
          </MDSText>
        </SectionListTitleWrapper>
      )}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="pinned-items">
          {provided => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {pinnedItems.map((item, index) => {
                const iconKind = (() => {
                  switch (item.itemKind) {
                    case "COLLECTION":
                      return (
                        <StyledCollectionIcon size={16} collectionId={item.itemId} legacy={false} />
                      );
                    case "NOTE":
                      return MdsIconKind.Document;
                    default:
                      return MdsIconKind.Question;
                  }
                })();

                const itemObservable =
                  item.itemKind === "COLLECTION"
                    ? store.collections.get(item.itemId)
                    : store.notes.get(item.itemId);

                return (
                  <Draggable key={item.itemId} draggableId={item.itemId} index={index}>
                    {provided => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <MDSSidebarSectionItem
                          label={item.title || "Untitled"}
                          iconKind={iconKind}
                          onClick={() => {
                            onClick?.();
                            switch (item.itemKind) {
                              case "COLLECTION":
                                store.navigation.goToCollection({
                                  collectionId: item.itemId,
                                });
                                break;
                              case "NOTE":
                                store.navigation.goToNote({
                                  noteId: item.itemId,
                                });
                                break;
                              default:
                                break;
                            }
                          }}
                          indent={indent}
                        >
                          <MDSButtonGroup slot={MDSSidebarSectionItemSlotName.ButtonsRight}>
                            <MDSTooltip
                              config={{
                                label: itemObservable?.isFavorited
                                  ? "Unpin from sidebar"
                                  : "Pin to sidebar",
                                placement: MDSTooltipPlacement.Top,
                              }}
                            >
                              <MDSIconButton
                                size={MDSIconButtonSize.XSmall}
                                iconKind={
                                  itemObservable?.isFavorited
                                    ? MdsIconKind.ThumbtackSolid
                                    : MdsIconKind.Thumbtack
                                }
                                variant={MDSIconButtonVariant.OnChrome}
                                onClick={() => {
                                  onClick?.();
                                  itemObservable?.toggleFavorite();
                                }}
                                rounded
                              />
                            </MDSTooltip>
                            <MdsIcon kind={MdsIconKind.Grip} />
                          </MDSButtonGroup>
                        </MDSSidebarSectionItem>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
});

const StyledCollectionIcon = styled(CollectionIcon)`
  .mds-icon {
    font-size: 11px;
    height: 11px;
    width: 11px;
  }
`;
